import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useSafetyNavigate as useNavigate } from "@/lib/safetyNavigation";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, EmptyState } from "./_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignaturePad } from "@/components/SignaturePad";
import { QATemplateManagerDialog } from "@/components/QATemplateManagerDialog";
import {
  Plus, ClipboardCheck, FileText, Camera, ImagePlus, Trash2, Check, X, Hammer, Loader2,
  AlertTriangle, Download, ChevronRight, CheckCircle2, Clock, ShieldCheck, CircleAlert, Settings2, Mail,
} from "lucide-react";
import { toast } from "sonner";
import { uploadMany } from "@/lib/imageUpload";
import { UploadingThumb } from "@/components/UploadingThumb";

// ============================================================
// Helpers
// ============================================================

const STATUS_LABEL = {
  draft: "Draft",
  pending_supervisor: "Supervisor sign-off",
  pending_office: "Office review",
  approved: "Approved",
  rejected: "Rejected",
};

const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border-zinc-300 dark:border-zinc-700",
  pending_supervisor: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 border-blue-300 dark:border-blue-800",
  pending_office: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border-amber-300 dark:border-amber-800",
  approved: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 border-emerald-300 dark:border-emerald-800",
  rejected: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 border-red-300 dark:border-red-800",
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold border rounded-sm ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

// ============================================================
// Main page
// ============================================================

export default function QA() {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const [records, setRecords] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [signoffRid, setSignoffRid] = useState(null);
  const [reviewRid, setReviewRid] = useState(null);
  const [emailFor, setEmailFor] = useState(null);
  const [editingRid, setEditingRid] = useState(null);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/qa/records");
      const rows = Array.isArray(data) ? data : [];
      const id = params.get("record");
      if (id && !rows.some(r => r.id === id)) {
        try { const result = await api.get(`/qa/records/${encodeURIComponent(id)}`); rows.unshift(result.data); }
        catch { toast.error("The linked QA record is unavailable or you do not have access."); }
      }
      setRecords(rows);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const id = params.get("record");
    if (!isOffice || !id) return;
    let live = true;
    api.get(`/qa/records/${encodeURIComponent(id)}`).then(({ data }) => {
      if (live) {
        setRecords(rows => [data, ...rows.filter(r => r.id !== id)]);
        if (data.status === "pending_office") setReviewRid(id);
      }
    }).catch(() => { if (live) toast.error("The linked QA record is unavailable or you do not have access."); });
    return () => { live = false; };
  }, [params, isOffice]);

  // Deep-link from quick-add: /app/qa?new=1 opens the form
  useEffect(() => {
    if (params.get("new") === "1") setFormOpen(true);
  }, [params]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return records.filter((r) =>
      (!params.get("record") || r.id === params.get("record")) &&
      (statusFilter === "all" || r.status === statusFilter) &&
      (!ql || [r.template_name, r.job_title, r.user_name, r.location, r.task_type, r.notes]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(ql))));
  }, [records, statusFilter, q, params]);

  const counts = useMemo(() => {
    const c = { all: records.length, draft: 0, pending_supervisor: 0, pending_office: 0, approved: 0, rejected: 0 };
    records.forEach((r) => { if (c[r.status] != null) c[r.status]++; });
    return c;
  }, [records]);

  return (
    <div data-testid="qa-page" className="p-4 lg:p-10 max-w-6xl mx-auto">
      {params.get("record") && <Link to={pathname} className="inline-flex min-h-11 items-center text-sm text-[#FF5F15]">← All QA records</Link>}
      <PageHeader
        tag="/ Quality Assurance"
        title="QA records"
        subtitle={isOffice
          ? "Civil task sign-offs from your crews — review, approve and export to PDF for engineers, councils or clients."
          : "Sign off your work as you finish — piles, drainage, retaining, concrete and more."}
        action={
          <div className="flex gap-2">
            {isOffice && (
              <Button
                variant="outline"
                data-testid="qa-templates-btn"
                onClick={() => setTemplatesDialogOpen(true)}
                className="rounded-sm border-2 border-zinc-800 dark:border-zinc-200 hover:bg-zinc-100 uppercase tracking-wider font-bold text-xs"
              >
                <Settings2 className="w-3.5 h-3.5 mr-1.5" /> Templates
              </Button>
            )}
            <Button
              data-testid="qa-new-btn"
              onClick={() => { setEditingRid(null); setFormOpen(true); }}
              className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-black font-bold"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New QA
            </Button>
          </div>
        }
      />

      {/* Status tabs */}
      <div className="relative max-w-sm mb-3">
        <ClipboardCheck className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search records, jobs, people…"
               className="pl-9 w-full rounded-lg border-zinc-300 dark:border-white/15 bg-white dark:bg-[#101620]"
               data-testid="qa-search-input" />
      </div>
      <Tabs value={statusFilter} onValueChange={setStatusFilter} className="mb-4">
        <TabsList className="grid grid-cols-3 md:flex md:flex-wrap rounded-sm bg-zinc-100 dark:bg-[#151C27] p-1 h-auto">
          <TabsTrigger value="all" data-testid="qa-tab-all" className="rounded-sm text-[12px] font-semibold">
            All <span className="ml-1.5 text-zinc-500">{counts.all}</span>
          </TabsTrigger>
          <TabsTrigger value="draft" data-testid="qa-tab-draft" className="rounded-sm text-[12px] font-semibold">
            Draft <span className="ml-1.5 text-zinc-500">{counts.draft}</span>
          </TabsTrigger>
          <TabsTrigger value="pending_supervisor" data-testid="qa-tab-supervisor" className="rounded-sm text-[12px] font-semibold">
            Supervisor <span className="ml-1.5 text-zinc-500">{counts.pending_supervisor}</span>
          </TabsTrigger>
          <TabsTrigger value="pending_office" data-testid="qa-tab-office" className="rounded-sm text-[12px] font-semibold">
            Office <span className="ml-1.5 text-zinc-500">{counts.pending_office}</span>
          </TabsTrigger>
          <TabsTrigger value="approved" data-testid="qa-tab-approved" className="rounded-sm text-[12px] font-semibold">
            Approved <span className="ml-1.5 text-zinc-500">{counts.approved}</span>
          </TabsTrigger>
          <TabsTrigger value="rejected" data-testid="qa-tab-rejected" className="rounded-sm text-[12px] font-semibold">
            Rejected <span className="ml-1.5 text-zinc-500">{counts.rejected}</span>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* List */}
      {!loaded ? (
        <div className="flex items-center justify-center py-16 text-zinc-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          title={records.length === 0 ? "No QA records yet" : "Nothing in this status"}
          description={records.length === 0
            ? (isOffice ? "Your crews haven't submitted any QA records yet. They'll appear here once field workers finish a task." : "Start your first QA record when you've finished a task.")
            : "Try a different filter."}
        />
      ) : (
        <div className="space-y-2.5" data-testid="qa-records-list">
          {filtered.map((r) => (
            <RecordCard
              key={r.id}
              record={r}
              isOffice={isOffice}
              onSignoff={() => setSignoffRid(r.id)}
              onReview={() => setReviewRid(r.id)}
              onEdit={() => { setEditingRid(r.id); setFormOpen(true); }}
              onPdf={() => downloadPdf(r)}
              onEmail={isOffice ? () => setEmailFor(r) : null}
              onReload={load}
            />
          ))}
        </div>
      )}

      {emailFor && <QAEmailDialog record={emailFor} onClose={() => setEmailFor(null)} onSent={() => { setEmailFor(null); load(); }} />}

      {/* Dialogs */}
      <QAFormDialog
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditingRid(null); }}
        editingId={editingRid}
        onSaved={(savedId, autoSignoff) => {
          load();
          if (autoSignoff) setSignoffRid(savedId);
        }}
      />
      <SupervisorSignoffDialog
        rid={signoffRid}
        onClose={() => setSignoffRid(null)}
        onSigned={() => { setSignoffRid(null); load(); }}
      />
      <OfficeReviewDialog
        rid={reviewRid}
        onClose={() => setReviewRid(null)}
        onReviewed={() => { setReviewRid(null); load(); }}
      />
      {isOffice && (
        <QATemplateManagerDialog
          open={templatesDialogOpen}
          onOpenChange={setTemplatesDialogOpen}
        />
      )}
    </div>
  );
}

function QAEmailDialog({ record, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    const emails = to.split(/[,;\s]+/).map((e) => e.trim()).filter((e) => e.includes("@"));
    if (!emails.length) { toast.error("Enter at least one email address"); return; }
    setSending(true);
    try {
      await api.post(`/qa/records/${record.id}/email`, { to: emails, message: message.trim() || null });
      toast.success(`QA record emailed to ${emails.join(", ")}`);
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="qa-email-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-xl">Email QA record</DialogTitle>
          <DialogDescription>
            Sends "{record.template_name}" as a branded PDF attachment (photos included).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="label-tag">To — separate multiple with commas</Label>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="engineer@client.co.nz, pm@council.govt.nz" className="mt-2 rounded-sm border-zinc-400" data-testid="qa-email-to" />
          </div>
          <div>
            <Label className="label-tag">Message (optional)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Kia ora — attached is the signed QA record for your files." className="mt-2 rounded-sm border-zinc-400 text-sm" data-testid="qa-email-message" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={sending}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="qa-email-send">
            <Mail className="w-3.5 h-3.5 mr-2" /> {sending ? "Sending…" : "Send PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

async function downloadPdf(record) {
  try {
    const res = await api.get(`/qa/records/${record.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `qa-${record.task_type}-${record.id.slice(0, 8)}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("PDF download failed");
  }
}

// ============================================================
// Record card
// ============================================================

function RecordCard({ record, isOffice, onSignoff, onReview, onEdit, onPdf, onEmail, onReload }) {
  const canFieldSignoff = record.status === "pending_supervisor";
  const canOfficeReview = isOffice && record.status === "pending_office";
  const canEdit = record.status === "draft" || (record.status === "rejected" && !isOffice);
  const canDelete = (record.status === "draft" && !isOffice) || isOffice;
  const completedAt = (record.submitted_at || record.created_at || "").slice(0, 10);

  const del = async () => {
    if (!window.confirm("Delete this QA record?")) return;
    try {
      await api.delete(`/qa/records/${record.id}`);
      toast.success("QA record deleted");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid={`qa-record-${record.id}`} className="hard-card p-4 hover:border-[#FF5F15] transition-colors">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 flex items-center justify-center bg-[#FF5F15]/10 text-[#FF5F15] shrink-0 rounded-sm">
          <ClipboardCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="font-semibold text-[14px] truncate">{record.template_name}</div>
              <div className="text-[12px] text-zinc-500 dark:text-zinc-400 truncate">
                {record.user_name}
                {record.job_title ? ` · ${record.job_title}` : ""}
                {record.location ? ` · ${record.location}` : ""}
              </div>
              <div className="text-[10px] font-mono text-zinc-400 mt-1 uppercase tracking-widest">
                {completedAt}
              </div>
            </div>
            <StatusBadge status={record.status} />
          </div>

          {/* Action row */}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            {canFieldSignoff && (
              <Button size="sm" data-testid={`qa-signoff-${record.id}`} onClick={onSignoff} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white">
                <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Supervisor sign-off
              </Button>
            )}
            {canOfficeReview && (
              <Button size="sm" data-testid={`qa-review-${record.id}`} onClick={onReview} className="rounded-sm bg-amber-500 hover:bg-amber-600 text-black">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Review
              </Button>
            )}
            {canEdit && (
              <Button size="sm" variant="outline" data-testid={`qa-edit-${record.id}`} onClick={onEdit} className="rounded-sm">
                Edit
              </Button>
            )}
            <Button size="sm" variant="outline" data-testid={`qa-pdf-${record.id}`} onClick={onPdf} className="rounded-sm">
              <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
            </Button>
            {onEmail && (
              <Button size="sm" variant="outline" data-testid={`qa-email-${record.id}`} onClick={onEmail} className="rounded-sm">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Email{record.emails_sent?.length ? ` ×${record.emails_sent.length}` : ""}
              </Button>
            )}
            {canDelete && (
              <Button size="sm" variant="ghost" data-testid={`qa-delete-${record.id}`} onClick={del} className="rounded-sm text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 ml-auto">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>

          {/* Office review note if rejected */}
          {record.status === "rejected" && record.office_review?.notes && (
            <div className="mt-3 p-2.5 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-[12px] text-red-800 dark:text-red-200">
              <div className="font-semibold mb-0.5 flex items-center gap-1.5"><CircleAlert className="w-3.5 h-3.5" /> Office feedback</div>
              {record.office_review.notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Form dialog — pick template → fill spec + checklist + photos
// ============================================================

function QAFormDialog({ open, onOpenChange, editingId, onSaved }) {
  const [templates, setTemplates] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [record, setRecord] = useState(null); // either being-built or being-edited
  const [tplId, setTplId] = useState("");
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState("pick"); // pick | fill

  // Load templates & jobs when opening
  useEffect(() => {
    if (!open) return;
    api.get("/qa/templates").then((r) => setTemplates(Array.isArray(r.data) ? r.data : []));
    api.get("/jobs").then((r) => setJobs(Array.isArray(r.data) ? r.data : [])).catch(() => setJobs([]));
  }, [open]);

  // If editing, fetch the record + set step
  useEffect(() => {
    if (!open || !editingId) return;
    api.get(`/qa/records/${editingId}`).then((r) => {
      setRecord(r.data);
      setTplId(r.data.template_id);
      setStep("fill");
    });
  }, [open, editingId]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setRecord(null);
      setTplId("");
      setStep("pick");
    }
  }, [open]);

  const selectedTpl = useMemo(() => templates.find((t) => t.id === tplId), [templates, tplId]);

  // After picking a template, create a draft and switch to fill step.
  const startFromTemplate = async (tpl) => {
    setSaving(true);
    try {
      const { data } = await api.post("/qa/records", {
        template_id: tpl.id,
        spec_values: {},
        checklist_responses: tpl.checklist.map((label) => ({ label, status: "pending" })),
        photos: [],
      });
      setRecord(data);
      setTplId(tpl.id);
      setStep("fill");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start QA");
    } finally {
      setSaving(false);
    }
  };

  const saveDraft = async ({ alsoSubmit = false } = {}) => {
    if (!record) return;
    setSaving(true);
    try {
      const patch = {
        job_id: record.job_id || null,
        location: record.location,
        weather: record.weather,
        spec_values: record.spec_values,
        checklist_responses: record.checklist_responses,
        photos: record.photos,
        field_notes: record.field_notes,
      };
      const { data } = await api.patch(`/qa/records/${record.id}`, patch);
      if (alsoSubmit) {
        const { data: submitted } = await api.post(`/qa/records/${record.id}/submit`);
        toast.success("QA submitted — get a site supervisor to sign off");
        onSaved(submitted.id, /* autoSignoff */ true);
        onOpenChange(false);
      } else {
        toast.success("Draft saved");
        onSaved(data.id, false);
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="qa-form-dialog">
        <DialogHeader>
          <DialogTitle>{step === "pick" ? "Pick a task type" : `QA · ${record?.template_name || ""}`}</DialogTitle>
          <DialogDescription>
            {step === "pick"
              ? "Choose what kind of work you've just finished — we'll show the matching checklist."
              : "Fill in the specs, tick the checklist, add photos if required, then submit for supervisor sign-off."}
          </DialogDescription>
        </DialogHeader>

        {step === "pick" && (
          <div className="space-y-1.5" data-testid="qa-template-picker">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                data-testid={`qa-tpl-${t.task_type}`}
                disabled={saving}
                onClick={() => startFromTemplate(t)}
                className="w-full text-left p-3 rounded-sm border border-zinc-200 dark:border-zinc-800 hover:border-[#FF5F15] hover:bg-[#FF5F15]/5 transition-colors flex items-center gap-3 disabled:opacity-50"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-[#FF5F15]/10 text-[#FF5F15] rounded-sm shrink-0">
                  <Hammer className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[13.5px]">{t.name}</div>
                  <div className="text-[11.5px] text-zinc-500 dark:text-zinc-400">
                    {t.checklist.length} checks · {t.spec_fields.length} specs
                    {t.photos_required ? " · 📷 photos required" : ""}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-400" />
              </button>
            ))}
            {templates.length === 0 && (
              <div className="text-center py-6 text-sm text-zinc-500">No templates available.</div>
            )}
          </div>
        )}

        {step === "fill" && record && selectedTpl && (
          <QAFormBody
            record={record}
            template={selectedTpl}
            jobs={jobs}
            onChange={setRecord}
          />
        )}

        {step === "fill" && (
          <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white dark:bg-[#0B1018] border-t border-zinc-200 dark:border-zinc-800 flex-col sm:flex-row gap-2">
            <Button variant="outline" data-testid="qa-save-draft" onClick={() => saveDraft({ alsoSubmit: false })} disabled={saving} className="rounded-sm w-full sm:w-auto">
              Save draft
            </Button>
            <Button data-testid="qa-submit-btn" onClick={() => saveDraft({ alsoSubmit: true })} disabled={saving} className="rounded-sm bg-[#FF5F15] hover:bg-[#e55410] text-black font-bold w-full sm:w-auto">
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
              Submit for sign-off
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Form body — spec values + checklist + photos
// ============================================================

function QAFormBody({ record, template, jobs, onChange }) {
  const set = (patch) => onChange({ ...record, ...patch });
  const setSpec = (key, value) => set({ spec_values: { ...(record.spec_values || {}), [key]: value } });
  const setItem = (idx, patch) => {
    const copy = [...record.checklist_responses];
    copy[idx] = { ...copy[idx], ...patch };
    set({ checklist_responses: copy });
  };

  const [uploads, setUploads] = useState([]); // in-flight photos: {key,url,pct,error}

  const uploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const items = files.map((f, i) => ({ key: `${Date.now()}-${i}`, file: f, url: URL.createObjectURL(f), pct: 0 }));
    setUploads((u) => [...u, ...items]);
    const base = record.photos || [];
    const added = [];
    await uploadMany(items, "qa", {
      onProgress: (key, pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x))),
      onDone: (key, res) => {
        added.push(res.path);
        set({ photos: [...base, ...added] });
        setUploads((u) => {
          const it = u.find((x) => x.key === key);
          if (it?.url) URL.revokeObjectURL(it.url);
          return u.filter((x) => x.key !== key);
        });
      },
      onError: (key, err) => {
        toast.error(err?.response?.data?.detail || "Photo upload failed");
        setUploads((u) => u.map((x) => (x.key === key ? { ...x, error: true } : x)));
        setTimeout(() => setUploads((u) => {
          const it = u.find((x) => x.key === key);
          if (it?.url) URL.revokeObjectURL(it.url);
          return u.filter((x) => x.key !== key);
        }), 4000);
      },
    });
  };

  const removePhoto = (i) => set({ photos: (record.photos || []).filter((_, idx) => idx !== i) });

  const decidedCount = (record.checklist_responses || []).filter((r) => r.status !== "pending").length;
  const totalCount = (record.checklist_responses || []).length;

  return (
    <div className="space-y-5">
      {/* Job + location */}
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Job (optional)</Label>
          <Select value={record.job_id || "__none__"} onValueChange={(v) => set({ job_id: v === "__none__" ? null : v })}>
            <SelectTrigger data-testid="qa-job-select" className="rounded-sm mt-1"><SelectValue placeholder="Select a job…" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.job_number ? `${j.job_number} · ` : ""}{j.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Location on site</Label>
          <Input data-testid="qa-location" value={record.location || ""} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. Lot 14, west boundary" className="rounded-sm mt-1" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Weather / conditions</Label>
          <Input data-testid="qa-weather" value={record.weather || ""} onChange={(e) => set({ weather: e.target.value })} placeholder="e.g. Fine, 18°C, light wind" className="rounded-sm mt-1" />
        </div>
      </div>

      {/* Spec values */}
      {(template.spec_fields || []).length > 0 && (
        <div>
          <div className="text-[11.5px] font-bold uppercase tracking-wide text-zinc-600 dark:text-zinc-400 mb-2">
            <span className="opacity-50">//</span> Specifications
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {template.spec_fields.map((f) => (
              <div key={f.key}>
                <Label className="text-[12px]">{f.label}</Label>
                {f.type === "select" ? (
                  <Select value={record.spec_values?.[f.key] || ""} onValueChange={(v) => setSpec(f.key, v)}>
                    <SelectTrigger data-testid={`qa-spec-${f.key}`} className="rounded-sm mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {(f.options || []).map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    data-testid={`qa-spec-${f.key}`}
                    type={f.type === "number" ? "number" : "text"}
                    value={record.spec_values?.[f.key] || ""}
                    onChange={(e) => setSpec(f.key, e.target.value)}
                    placeholder={f.suffix ? `e.g. 100 ${f.suffix}` : ""}
                    className="rounded-sm mt-1"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Checklist */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11.5px] font-bold uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
            <span className="opacity-50">//</span> Checklist
          </div>
          <div className="text-[11px] font-mono text-zinc-500">{decidedCount}/{totalCount} decided</div>
        </div>
        <div className="space-y-1.5" data-testid="qa-checklist">
          {(record.checklist_responses || []).map((r, i) => (
            <ChecklistRow
              key={i}
              row={r}
              testIdx={i}
              onChange={(patch) => setItem(i, patch)}
            />
          ))}
        </div>
      </div>

      {/* Photos */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11.5px] font-bold uppercase tracking-wide text-zinc-600 dark:text-zinc-400">
            <span className="opacity-50">//</span> Photos {template.photos_required && <span className="text-red-500 ml-1">*</span>}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-[11px] font-semibold text-[#FF5F15] cursor-pointer hover:underline" data-testid="qa-photo-camera-label">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                data-testid="qa-photo-input"
                onChange={(e) => uploadPhotos(e.target.files)}
                className="hidden"
              />
              <Camera className="w-3.5 h-3.5 inline mr-1" /> Camera
            </label>
            <label className="text-[11px] font-semibold text-[#FF5F15] cursor-pointer hover:underline" data-testid="qa-photo-gallery-label">
              <input
                type="file"
                accept="image/*"
                multiple
                data-testid="qa-photo-gallery-input"
                onChange={(e) => uploadPhotos(e.target.files)}
                className="hidden"
              />
              <ImagePlus className="w-3.5 h-3.5 inline mr-1" /> Gallery
            </label>
          </div>
        </div>
        {template.photos_required && (record.photos || []).length === 0 && (
          <div className="text-[11px] text-red-600 dark:text-red-400 flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5" /> At least one photo is required to submit.
          </div>
        )}
        {((record.photos || []).length > 0 || uploads.length > 0) && (
          <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
            {record.photos.map((p, i) => (
              <div key={i} className="relative aspect-square rounded-sm border border-zinc-200 dark:border-zinc-800 overflow-hidden" data-testid={`qa-photo-${i}`}>
                <img
                  src={`${api.defaults.baseURL}/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`}
                  alt={`Photo ${i + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  data-testid={`qa-photo-remove-${i}`}
                  className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white rounded-sm flex items-center justify-center hover:bg-red-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {uploads.map((u) => <UploadingThumb key={u.key} up={u} className="rounded-sm" />)}
          </div>
        )}
      </div>

      {/* Notes */}
      <div>
        <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Notes (optional)</Label>
        <Textarea
          data-testid="qa-field-notes"
          value={record.field_notes || ""}
          onChange={(e) => set({ field_notes: e.target.value })}
          placeholder="Any other comments about the work…"
          className="rounded-sm mt-1 min-h-20"
        />
      </div>
    </div>
  );
}

function ChecklistRow({ row, testIdx, onChange }) {
  const [notesOpen, setNotesOpen] = useState(false);
  return (
    <div className={`p-2.5 rounded-sm border ${
      row.status === "pass" ? "border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-800"
      : row.status === "fail" ? "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-800"
      : row.status === "na" ? "border-zinc-300 bg-zinc-50 dark:bg-[#151C27] dark:border-zinc-700"
      : "border-zinc-200 dark:border-zinc-800"
    }`}>
      <div className="flex items-start gap-2.5">
        <div className="flex-1 text-[13.5px] font-medium">{row.label}</div>
        <div className="flex gap-1 shrink-0">
          {[
            { v: "pass", l: "Pass", cls: "bg-emerald-600 text-white hover:bg-emerald-700" },
            { v: "fail", l: "Fail", cls: "bg-red-600 text-white hover:bg-red-700" },
            { v: "na", l: "N/A", cls: "bg-zinc-500 text-white hover:bg-zinc-600" },
          ].map((b) => (
            <button
              key={b.v}
              type="button"
              data-testid={`qa-check-${testIdx}-${b.v}`}
              onClick={() => onChange({ status: b.v })}
              className={`px-2.5 py-1 text-[11px] font-bold uppercase rounded-sm transition-all ${
                row.status === b.v ? b.cls : "bg-white dark:bg-[#151C27] text-zinc-700 dark:text-zinc-300 border border-zinc-300 dark:border-zinc-700 hover:border-zinc-500"
              }`}
            >
              {b.l}
            </button>
          ))}
        </div>
      </div>
      {(row.status === "fail" || notesOpen || row.notes) && (
        <Input
          value={row.notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Notes (recommended on fail)"
          className="rounded-sm mt-2 text-[12.5px] h-8"
          data-testid={`qa-check-notes-${testIdx}`}
        />
      )}
      {!notesOpen && !row.notes && row.status !== "fail" && (
        <button
          type="button"
          onClick={() => setNotesOpen(true)}
          className="text-[11px] text-zinc-500 hover:text-[#FF5F15] mt-1.5"
        >
          + Add note
        </button>
      )}
    </div>
  );
}

// ============================================================
// Supervisor sign-off dialog
// ============================================================

function SupervisorSignoffDialog({ rid, onClose, onSigned }) {
  const [supName, setSupName] = useState("");
  const [sig, setSig] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (rid) { setSupName(""); setSig(null); setNotes(""); }
  }, [rid]);

  const submit = async () => {
    if (!supName.trim()) { toast.error("Supervisor name required"); return; }
    if (!sig) { toast.error("Signature required"); return; }
    setSaving(true);
    try {
      await api.post(`/qa/records/${rid}/supervisor-signoff`, {
        supervisor_name: supName.trim(),
        signature: sig,
        notes: notes.trim() || null,
      });
      toast.success("Sign-off recorded — sent to office for review");
      onSigned();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-off failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!rid} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="qa-signoff-dialog">
        <DialogHeader>
          <DialogTitle>Site supervisor sign-off</DialogTitle>
          <DialogDescription>Pass the phone to your site supervisor. They&apos;ll type their name and sign below.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Supervisor name</Label>
            <Input data-testid="qa-supervisor-name" value={supName} onChange={(e) => setSupName(e.target.value)} placeholder="Full name" className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Signature</Label>
            <div className="mt-1 border border-zinc-300 dark:border-zinc-700 rounded-sm overflow-hidden">
              <SignaturePad onSave={setSig} onCancel={() => setSig(null)} testIdPrefix="qa-sig" />
            </div>
            {sig && <div className="text-[11px] text-emerald-600 mt-1">✓ Signature captured</div>}
          </div>
          <div>
            <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Notes (optional)</Label>
            <Textarea data-testid="qa-supervisor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm mt-1 min-h-16" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button data-testid="qa-signoff-submit" onClick={submit} disabled={saving} className="rounded-sm bg-blue-600 hover:bg-blue-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1.5" />}
            Sign off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// Office review dialog
// ============================================================

function OfficeReviewDialog({ rid, onClose, onReviewed }) {
  const [record, setRecord] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!rid) { setRecord(null); setNotes(""); return; }
    api.get(`/qa/records/${rid}`).then((r) => setRecord(r.data));
  }, [rid]);

  const decide = async (decision) => {
    setSaving(true);
    try {
      await api.post(`/qa/records/${rid}/review`, { decision, notes: notes.trim() || null });
      toast.success(decision === "approved" ? "QA approved" : "QA rejected — field worker can amend and resubmit");
      onReviewed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Review failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!rid} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="qa-review-dialog">
        <DialogHeader>
          <DialogTitle>Review QA · {record?.template_name || ""}</DialogTitle>
          <DialogDescription>
            Submitted by {record?.user_name} on {(record?.submitted_at || "").slice(0, 10)} · signed off by {record?.supervisor_signoff?.supervisor_name || "—"}
          </DialogDescription>
        </DialogHeader>
        {record ? (
          <div className="space-y-4 text-[13px]">
            <ReviewSummary record={record} />
            <div>
              <Label className="text-[11.5px] font-semibold uppercase tracking-wide">Office notes (shown to field worker if rejected)</Label>
              <Textarea data-testid="qa-review-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-sm mt-1 min-h-20" />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center py-8 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…</div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" data-testid="qa-review-cancel">Cancel</Button>
          <Button onClick={() => decide("rejected")} disabled={saving} variant="destructive" className="rounded-sm" data-testid="qa-review-reject">
            <X className="w-4 h-4 mr-1.5" /> Reject
          </Button>
          <Button onClick={() => decide("approved")} disabled={saving} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="qa-review-approve">
            <Check className="w-4 h-4 mr-1.5" /> Approve
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewSummary({ record }) {
  const failCount = (record.checklist_responses || []).filter((r) => r.status === "fail").length;
  const passCount = (record.checklist_responses || []).filter((r) => r.status === "pass").length;
  const naCount = (record.checklist_responses || []).filter((r) => r.status === "na").length;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="p-2 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-sm">
          <div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{passCount}</div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400">Pass</div>
        </div>
        <div className={`p-2 ${failCount > 0 ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800" : "bg-zinc-50 dark:bg-[#151C27] border-zinc-200 dark:border-zinc-800"} border rounded-sm`}>
          <div className={`text-2xl font-bold ${failCount > 0 ? "text-red-700 dark:text-red-300" : "text-zinc-500"}`}>{failCount}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Fail</div>
        </div>
        <div className="p-2 bg-zinc-50 dark:bg-[#151C27] border border-zinc-200 dark:border-zinc-800 rounded-sm">
          <div className="text-2xl font-bold text-zinc-500">{naCount}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">N/A</div>
        </div>
      </div>
      {failCount > 0 && (
        <div className="p-2.5 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-[12px] text-red-800 dark:text-red-200">
          <div className="font-semibold mb-1">Failed items:</div>
          <ul className="list-disc list-inside space-y-0.5">
            {record.checklist_responses.filter((r) => r.status === "fail").map((r, i) => (
              <li key={i}>{r.label}{r.notes ? ` — ${r.notes}` : ""}</li>
            ))}
          </ul>
        </div>
      )}
      {record.field_notes && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Field notes</div>
          <div className="text-[12.5px] p-2.5 bg-zinc-50 dark:bg-[#151C27] rounded-sm border border-zinc-200 dark:border-zinc-800">{record.field_notes}</div>
        </div>
      )}
      {(record.photos || []).length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Photos ({record.photos.length})</div>
          <div className="grid grid-cols-4 gap-1.5">
            {record.photos.map((p, i) => (
              <img
                key={i}
                src={`${api.defaults.baseURL}/files/view?path=${encodeURIComponent(p)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`}
                alt={`Photo ${i + 1}`}
                className="aspect-square w-full object-cover rounded-sm border border-zinc-200 dark:border-zinc-800"
              />
            ))}
          </div>
        </div>
      )}
      {record.supervisor_signoff?.signature && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Supervisor signature · {record.supervisor_signoff.supervisor_name}</div>
          <img src={record.supervisor_signoff.signature} alt="Signature" className="bg-white border border-zinc-200 dark:border-zinc-800 rounded-sm max-h-24" />
        </div>
      )}
    </div>
  );
}
